import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Stack,
  Button,
  CircularProgress,
  Fab,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { fetchDataEngine } from "../../../../data/Axios/DynamicService";
import { caseManagementUrl } from "../../../../config";
import MainCard from "../../../../components/MainCard";
import AnalyticsCard from "../../../../components/AnalyticsCard";
import PieChart from "../../../../components/PieChart";
import { format, isValid } from "date-fns"; // Ensure date-fns is imported
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";

const base_url = caseManagementUrl.uri;
// const company_url = companyManagementUrl.uri;

export default function CaseDashboard() {
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const caseId = 
    location.state?.caseId ||
    JSON.parse(localStorage.getItem("CaseId"));
  
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState([]);
  const [caseData, setCaseData] = useState([]);
  const [cards, setCards] = useState([]);
  const navigate = useNavigate();

  const handleGoBack = () => {
    const backPath = location.state?.from || "/Cases";
    navigate(backPath);
  };

  const [loading, setLoading] = useState(true);

  const [progress, setProgress] = useState(90);

  const Item = styled(Box)(({ theme }) => ({
    backgroundColor: `${colors.primary[400]}}`,
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
  }));

  const safeFormatDate = (dateString) => {
    const date = new Date(dateString);
    return isValid(date) ? format(date, "MM/dd/yyyy") : "Invalid date";
  };

  async function fetchData() {
    try {
      const returnFields = [
        "numberOfTasks",
        "numberOfDocuments",
        "tasksPending",
        "tasksInProgress",
        "tasksOnHold",
        "tasksCompleted",
        "tasksCancelled",
        "tasksFailed",
      ];

      const inputFields = {
        caseId: caseId,
      };
      const Facts = await fetchDataEngine(
        "caseSummary",
        "case",
        inputFields,
        returnFields,
        base_url
      );

      setData(Facts);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchUpcomingCaseEvents = async () => {
    try {
      const returnFields = ["eventDate", "eventType", "description"];

      const inputFields = {
        caseId: caseId,
      };

      const upcoming = await fetchDataEngine(
        "allCaseEventsByColumnValues",
        "caseEvent",
        inputFields,
        returnFields,
        base_url
      );

      setEvents(upcoming);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingCaseEvents();
  }, []);

  async function fetchData() {
    try {
      const returnFields = [
        "id",
        "username",
        "statusFlag",
        "assignedDate",
        "firstName",
        "lastName",
        "email",
      ];
      const inputFields = {
        caseId: caseId,
      };
      const TeamAssignmentsData = await fetchDataEngine(
        "teamAssignmentViewsByColumnValues",
        "viewModel",
        inputFields,
        returnFields,
        base_url
      );
      setTeam(TeamAssignmentsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchCaseData() {
    try {
      const returnFields = [
        "id",
        "description",
        "caseNumber",
        "fileNumber",
        "title",
        "createdDate",
        "caseCategory",
        "summary",
        "fileNumber"
      ];
      const inputFields = { id: caseId };
      const Case = await fetchDataEngine(
        "casebyId",
        "caseModel",
        inputFields,
        returnFields,
        base_url
      );
      console.log("CaseData", Case)
      setCaseData(Case);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchCaseData();
  }, []);

  async function fetchCaseSummaryData() {
    try {
      const returnFields = [
        "numberOfTasks",
        "numberOfDocuments",
        "tasksPending",
        "tasksInProgress",
        "tasksOnHold",
        "tasksCompleted",
        "tasksCancelled",
        "tasksFailed",
      ];
      const inputFields = { caseId: caseId };
      const Cards = await fetchDataEngine(
        "caseSummary",
        "case",
        inputFields,
        returnFields,
        base_url
      );
      setCards(Cards);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchCaseSummaryData();
  }, []);

  return (
    <>
    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
      {/* Floating Back Button */}
      <Fab
        color="secondary"
        aria-label="go back"
        onClick={handleGoBack}
        sx={{
          position: "fixed",
          top: 120,      
          left: 260,     
          zIndex: 1100,
          width: 35,
          height: 35,
          boxShadow: 3,
          "&:hover": {
          boxShadow: 6,
          },
        }}
      >
      <ArrowBackIcon />
      </Fab>
        
    </Box>
    
      <Grid container rowSpacing={1} columnSpacing={1}>
        <Grid
          item
          lg={6}
          md={6}
          xs={16}
          sx={{
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[600]}`,
            borderRadius: "0.3rem",
            padding: "1rem",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              fontWeight="600"
              sx={{ marginBottom: "1rem", color: colors.grey[100] }}
            >
              All Case Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    File Number
                  </Typography>
                  <Typography variant="body2">{caseData.fileNumber}</Typography>
                </Item>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    File Number
                  </Typography>
                  <Typography variant="body2">
                    {caseData.fileNumber || "—"}
                  </Typography>
                </Item>
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    Case Title
                  </Typography>
                  <Typography variant="body2">{caseData.title}</Typography>
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {caseData.description}
                  </Typography>
                </Item>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    Category
                  </Typography>
                  <Typography variant="body2">
                    {caseData.caseCategory}
                  </Typography>
                </Item>
              </Grid> */}
              {/* <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    Summary
                  </Typography>
                  <Typography variant="body2">{caseData.summary}</Typography>
                </Item>
              </Grid> */}
              <Grid item xs={12} sm={6} md={6}>
                <Item>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={colors.greenAccent[400]}
                  >
                    Created Date
                  </Typography>
                  <Typography variant="body2">
                  {safeFormatDate(caseData.createdDate)}
                  </Typography>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid
          item
          lg={6}
          md={6}
          xs={12}
          sx={{
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[600]}`,
            borderRadius: "0.3rem",
            // margin: "1rem", // Adjust margin as needed
            padding: "1rem", 
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Item>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{ marginBottom: "0.5rem" }}
                >
                  Case Insights
                </Typography>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    sx={{ borderRadius: "2px", color: "yellowgreen" }}
                  >
                    <AnalyticsCard
                      title="Number Of Tasks"
                      count={cards.numberOfTasks}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <AnalyticsCard
                      title="Tasks in Progress"
                      count={cards.tasksInProgress}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <AnalyticsCard
                      title="Pending Tasks"
                      count={cards.tasksPending}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <AnalyticsCard
                      title="Uploaded Documents"
                      count={cards.numberOfDocuments}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Item>
          </Box>
        </Grid>
        <Grid
          item
          lg={6}
          md={6}
          xs={12}
          sx={{
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[600]}`,
            borderRadius: "0.3rem",
            padding: "1rem",
            marginTop: "0.5rem",
          }}
        >
          <Item>
            <Box
              sx={{
                flexGrow: 1,
                padding: "0.5rem",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                  // backgroundColor={colors.primary[400]}
                  // overflow="auto"
                  // borderRadius="10px"
                  // border={`1px solid ${colors.grey[600]}`}
                  >
                    <Typography variant="h5" fontWeight="600">
                      Team Assignment
                    </Typography>

                    <Box>
                      <TableContainer
                        component={Paper}
                        sx={{ bgcolor: colors.primary[400] }}
                      >
                        <Table sx={{ bgcolor: colors.primary[400] }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>First Name</TableCell>
                              <TableCell>Last Name</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!loading && team.length > 0 ? (
                              team.map((team, index) => (
                                <TableRow key={index}>
                                  <TableCell>{team.firstName}</TableCell>
                                  <TableCell>{team.lastName}</TableCell>
                                  <TableCell>
                                    {format(
                                      new Date(team.assignedDate),
                                      "MM/dd/yyyy"
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} align="center">
                                  {loading ? "Loading..." : "No events found"}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Item>
        </Grid>
        <Grid
          item
          lg={6}
          md={6}
          xs={12}
          sx={{
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[600]}`,
            borderRadius: "0.3rem",
            padding: "1rem",
            marginTop: "0.5rem",
          }}
        >
          <Item>
            <Typography
              variant="h5"
              fontWeight="600"
              sx={{ marginBottom: "0.5rem" }}
            >
              Upcoming Events
            </Typography>
            <Box
              sx={{
                bgcolor: colors.primary[400],
                borderRadius: "10px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box>
                <TableContainer
                  component={Paper}
                  sx={{ bgcolor: colors.primary[400] }}
                >
                  <Table sx={{ bgcolor: colors.primary[400] }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>S/N</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.length > 0 ? (
                        events.map((event, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              {format(new Date(event.eventDate), "MM/dd/yyyy")}
                            </TableCell>
                            <TableCell>{event.description}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            {loading ? "Loading..." : "No events found"}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Item>
        </Grid>
        {/* <Grid item lg={6} md={6} xs={12}>
          <Item>
            <Typography variant="h5" fontWeight="600">
              Task Breakdown
            </Typography>
            <Box>
              <MainCard content={false}>
                <Box sx={{ pb: 0 }}>
                  <List>
                    <ListItemButton divider>
                      <ListItemText primary="Team Assignment" />
                      <Typography variant="h5">
                        {data.numberOfDocuments}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Task Allocation" />
                      <Typography variant="h5">{data.numberOfTasks}</Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Event Recording " />
                      <Typography variant="h5">{data.tasksOnHold}</Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Judge Assignment" />
                      <Typography variant="h5">
                        {data.numberOfDocuments}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Fact recording" />
                      <Typography variant="h5">
                        {data.tasksCompleted}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Court Assignment" />
                      <Typography variant="h5">{data.numberOfTasks}</Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Document Upload" />
                      <Typography variant="h5">{data.tasksPending}</Typography>
                    </ListItemButton>
                  </List>
                </Box>
              </MainCard>
            </Box>
          </Item>
        </Grid> */}
      </Grid>
    </>
  );
}
